import { Input } from '../../Inputs/Input';
import { Button } from '../../Button';
import { formRules } from '../FormRules';
import { ChangeEvent, useEffect, useState } from 'react';
import { apiService } from '../../../services/api';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector, useToast } from '../../../hooks';
import { InputAutocomplete } from '../../Inputs/Autocomplete';
import { formatPhone, removeNonNumericChars } from '../../../utils/formatters';
import { DataAutocomplete } from '../../SelectResponsibles';
import { Notification } from '../NotificationComponent';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Datepicker } from '../../Datepicker';
import dayjs, { Dayjs } from 'dayjs';
import { UsersFormProps } from '../../../pages/Register/form-props.types';
import { UpdateUserReq, UserRole } from '../../../services/api.types';
import { usersService } from '../../../services/users.service';
import { Switch } from '../../Switch';
import { useNavigate } from 'react-router-dom';

interface StudentFormValues {
  fullName: string;
  displayName: string;
  responsibleName: DataAutocomplete | null;
  email?: string;
  active?: boolean;
  role?: UserRole;
  phone?: string;
  birthdate: Dayjs | null;
  studentWithAccess: boolean;
  notification: {
    //notifySMS: boolean;
    notifyEmail: boolean;
    notifyWhatsapp: boolean;
  };
}

export function StudentRegisterForm({
  mode,
  userToUpdate,
}: Readonly<UsersFormProps>) {
  const toast = useToast();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const [responsibles, setResponsibles] = useState<DataAutocomplete[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const selfEdit = userToUpdate?.id === user?.id;
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;

  const getStudentDefaultValues = (): StudentFormValues => {
    if (userToUpdate && mode === 'UPDATE') {
      return {
        ...userToUpdate,
        ...userToUpdate.student,
        email: userToUpdate.email ?? '',
        responsibleName: null,
        active: userToUpdate.active,
        phone: userToUpdate.student.phone
          ? formatPhone(userToUpdate.student.phone)
          : '',
        notification: {
          notifyEmail: userToUpdate.notifyEmail,
          notifyWhatsapp: userToUpdate.notifyWhatsapp,
        },
        birthdate: dayjs(userToUpdate.student.birthdate),
        studentWithAccess: !!userToUpdate.email,
      };
    }

    return {
      fullName: '',
      displayName: '',
      responsibleName: null,
      email: '',
      phone: '',
      birthdate: null,
      studentWithAccess: true,
      notification: {
        notifyEmail: true,
        notifyWhatsapp: true,
      },
    };
  };

  const methods = useForm<StudentFormValues>({
    mode: 'all',
    defaultValues: getStudentDefaultValues(),
  });

  const fullName = methods.watch('fullName');
  const studentWithAccess = methods.watch('studentWithAccess');

  useEffect(() => {
    if (mode === 'CREATE') {
      methods.setValue('displayName', fullName);
    }
  }, [fullName, mode, methods]);

  useEffect(() => {
    if (userToUpdate) {
      if (user?.role === UserRole.STUDENT) {
        methods.setValue('responsibleName', {
          id: userToUpdate.student.responsibleId,
          displayName: userToUpdate.student.responsible?.fullName ?? '',
        });
      }
    }
  }, [userToUpdate, methods]);

  useEffect(() => {
    if (isAdminOrEmployee) {
      apiService.getAllResponsibles().then((response) => {
        if (mode === 'UPDATE' && userToUpdate) {
          const responsible = response.find(
            (responsible) =>
              responsible.id === userToUpdate.student.responsibleId
          );
          methods.setValue('responsibleName', {
            id: userToUpdate.student.responsibleId,
            displayName: responsible?.user.fullName ?? '',
          });
        }
        setResponsibles(
          response.map((responsible) => ({
            id: responsible.id,
            displayName: responsible.user.fullName,
          }))
        );
      });
    }
  }, []);

  const onSubmit = async (data: StudentFormValues) => {
    const {
      responsibleName,
      phone: phoneFormatted,
      notification,
      fullName,
      birthdate: birthdateDayjs,
      email,
      studentWithAccess,
      ...newData
    } = data;

    const phone = phoneFormatted
      ? removeNonNumericChars(phoneFormatted)
      : undefined;

    const birthdate = birthdateDayjs?.toDate();

    const updateAPImethod = (data: UpdateUserReq) =>
      userToUpdate && !selfEdit
        ? usersService.updateUserById(userToUpdate.id, data)
        : usersService.updateSelfUser(data);

    if (mode === 'UPDATE') {
      const updateUserReq: UpdateUserReq = {
        ...notification,
        displayName: newData.displayName,
        role: newData.role,
        [UserRole.STUDENT]: {},
      };

      if (isAdminOrEmployee) {
        updateUserReq.fullName = fullName;
        updateUserReq.email = studentWithAccess ? email : undefined;
        updateUserReq.active = newData.active;

        updateUserReq[UserRole.STUDENT] = {
          studentWithAccess: studentWithAccess,
          phone: studentWithAccess ? phone : undefined,
          responsibleId: responsibleName?.id,
          birthdate: birthdate,
        };
      }

      setIsLoading(true);
      await updateAPImethod(updateUserReq)
        .then(() => {
          toast.success('Aluno alterado!');
          navigate(-1);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      if (!birthdate) {
        toast.error('Data de nascimento inválida');
        return;
      }

      setIsLoading(true);
      await apiService
        .registerStudent({
          ...newData,
          ...notification,
          studentWithAccess: studentWithAccess,
          email: email,
          birthdate: birthdate,
          fullName: fullName,
          phone: phone,
          responsibleId: responsibleName?.id ?? 0,
        })
        .then(() => {
          methods.reset(getStudentDefaultValues());
          toast.success('Aluno cadastrado!');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleChangeMaskPhone = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    methods.setValue('phone', formatPhone(event.target.value));
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="flex flex-col items-center gap-2 w-full box-border pt-2 p-4 justify-between"
      >
        <Input
          name="fullName"
          label="Nome Completo"
          rules={formRules.fullNameRequired}
          disabled={selfEdit}
        />
        <Input name="displayName" label="Como gostaria de ser chamado(a)?" />

        <InputAutocomplete
          name="responsibleName"
          label="Nome do Responsável"
          CustomOnChange={(event, value) => {
            methods.setValue('responsibleName', value);
          }}
          values={responsibles}
          rules={formRules.responsibleNameRequired}
          disabled={selfEdit}
        />

        {isAdminOrEmployee && (
          <Switch
            label="O aluno tem acesso ao sistema?"
            name="studentWithAccess"
          />
        )}

        {studentWithAccess && (
          <>
            <Input
              name="email"
              type="email"
              label="Email"
              rules={formRules.emailRequired}
              disabled={selfEdit}
            />

            <Input
              name="phone"
              label="Telefone"
              rules={formRules.phoneRequired}
              onChange={handleChangeMaskPhone}
              disabled={selfEdit}
            />
          </>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <Datepicker
            className="w-full"
            labelOut
            name="birthdate"
            label="Data de Nascimento"
            rules={formRules.birthdateRequired}
            disabled={selfEdit}
          />
        </LocalizationProvider>

        {studentWithAccess && (
          <Notification
            label="Notificações"
            name="notification"
            disableEmailField={mode === 'CREATE'}
          />
        )}

        <Button type="submit" isLoading={isLoading} circular>
          {mode === 'CREATE' ? 'Cadastrar' : 'Alterar'}
        </Button>
      </form>
    </FormProvider>
  );
}
